import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Typography,
  Grid,
  Button,
  DialogActions,
  Box,
  TextField
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { KioskOrderingStore } from 'kiolyn-api';
import { useObserver } from 'mobx-react-lite';
import React, { useEffect, FC, Fragment, useState } from 'react';
import { ViewState } from '../../../domain/models/ViewState';
import { useOrdering } from '../../../stores/OrderingStore';
import { LayoutVariant } from '../../Core/CommonTypes';
import OrderSummary from '../OrderDetail/OrderSummary';
import SubmitSteps from '../SubmitOnlineOrder/SubmitSteps/SubmitSteps';
import TipGuides from '../SubmitOnlineOrder/TipGuides/TipGuides';
import Thanks from '../Thanks/Thanks';

const useStyles = makeStyles(theme => ({
  paper: {
    height: 'calc(100% - 96px)'
  },
  close: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1)
  },
  submitContainer: {
    position: 'relative',
    top: '25%'
  },
  message: {
    margin: theme.spacing(2),
    textAlign: 'center'
  },
  submit: {
    margin: theme.spacing(2)
  },
  orderDetail: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(4),
    padding: theme.spacing(1),
    minWidth: 320
  }
}));

interface SubmitKioskOrderProps {
  onClose?: () => void;
}

const SubmitKioskOrder: FC<SubmitKioskOrderProps> = ({ onClose }) => {
  const classes = useStyles();
  const orderingStore = useOrdering();
  const order = orderingStore.order;
  const kioskStore = orderingStore.store as KioskOrderingStore;
  const acceptTip = kioskStore.kiosk_tip_settings?.enable_tip_guides || false;
  const [activeStep, setActiveStep] = useState(1);

  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [applyPromo, setApplyPromo] = useState(false);

  if (kioskStore.promo_codes && promoCodeInput === '') {
    const appliedPromoCode = kioskStore.promo_codes.find(i => i.apply_to_kiosk);
    if (appliedPromoCode) {
      setPromoCodeInput(appliedPromoCode.name);
    }
  }

  useEffect(() => {
    if (
      orderingStore.submitState === ViewState.OK ||
      orderingStore.submitState === ViewState.SUBMITTED ||
      orderingStore.submitState === ViewState.PRINTFAILED
    ) {
      setTimeout(() => {
        if (onClose !== undefined) {
          onClose();
        }
      }, 10000);
    }
  }, [orderingStore.submitState, onClose]);

  const applyPromoCode = () => {
    if (kioskStore.promo_codes && promoCodeInput !== '') {
      const appliedPromoCode = kioskStore.promo_codes.find(
        i => i.name === promoCodeInput
      );
      if (appliedPromoCode) {
        orderingStore.setPromoCode(appliedPromoCode);
      }
    }
  };

  const handleOnClose = () => {
    if (onClose !== undefined) {
      onClose();
    }
  };

  const tipGuides = (
    <TipGuides
      key="tip"
      total={order.total}
      tipGuides={kioskStore.kiosk_tip_settings?.tip_guides}
      value={order.tip}
      isVirtualKeyboard={true}
      onChange={tip => {
        order.tip = tip;
        orderingStore.updateOrderAmounts();
      }}
    />
  );

  const submit = useObserver(() => {
    switch (orderingStore.submitState) {
      case ViewState.LOADING:
        return (
          <Grid
            container
            justify="center"
            alignItems="center"
            direction="column"
          >
            <Typography variant="h6" align="center" color="secondary">
              Please insert your card or tap your phone.
            </Typography>
            <Typography
              variant="h6"
              color="textSecondary"
              className={classes.message}
              gutterBottom
            >
              To Cancel, press this button.
            </Typography>
            <img
              src={process.env.PUBLIC_URL + '/cancel-button.png'}
              alt={'Cancel'}
              style={{ cursor: 'pointer' }}
              onClick={() => handleOnClose()}
            />
          </Grid>
        );
      case ViewState.ERROR:
        return (
          <Grid
            container
            justify="center"
            alignItems="center"
            direction="column"
          >
            <Typography
              variant="h6"
              color="textSecondary"
              className={classes.message}
              gutterBottom
            >
              There was error submitting your order.
              <br />
              Please try again or contact our cashier for support.
            </Typography>
            <Grid
              container
              justify="center"
              alignItems="center"
              direction="row"
            >
              <Button
                className={classes.submit}
                variant="contained"
                size="large"
                color="primary"
                aria-label="pay submit order"
                onClick={() => orderingStore.submitCurrentOrder(false)}
              >
                SUBMIT YOUR ORDER AGAIN
              </Button>
              <Button
                className={classes.submit}
                variant="contained"
                size="large"
                color="primary"
                aria-label="pay submit order"
                onClick={() => orderingStore.submitCurrentOrder(true)}
              >
                PAY WITH CASHIER
              </Button>
            </Grid>
          </Grid>
        );
      case ViewState.OK:
        return (
          <Thanks isSendEmail={true}>
            <Typography variant="h6" color="textSecondary" align="center">
              Payment Successful. Please take your receipt.
            </Typography>
          </Thanks>
        );
      case ViewState.SUBMITTED:
        return (
          <Thanks isSendEmail={true}>
            <Typography variant="h6" color="textSecondary" align="center">
              Your order has been submitted. Please contact our cashier to
              complete the order.
            </Typography>
          </Thanks>
        );
      case ViewState.PRINTFAILED:
        return (
          <Thanks isSendEmail={true}>
            <Typography variant="h6" color="textSecondary" align="center">
              Your transaction has been approved.
            </Typography>
            <Typography variant="h6" color="textSecondary" align="center">
              Your order is being prepared.
            </Typography>
            <Typography variant="h6" color="textSecondary" align="center">
              Print has an issue. Please collect your receipt from cashier.
            </Typography>
          </Thanks>
        );
      case ViewState.NONE:
        return (
          <Grid
            container
            justify="center"
            alignItems="center"
            direction="column"
          >
            <Box>
              <Typography
                variant="h6"
                color="textSecondary"
                className={classes.message}
                gutterBottom
              >
                Do you want to submit your order now?
              </Typography>
              <Grid item container direction="row" justify="center">
                <Button
                  className={classes.submit}
                  variant="contained"
                  size="large"
                  color="primary"
                  aria-label="pay submit order"
                  onClick={() => orderingStore.submitCurrentOrder(false)}
                >
                  SUBMIT YOUR ORDER
                </Button>
              </Grid>
              <Grid
                container
                direction="row"
                alignContent="center"
                justify="center"
              >
                <Box>
                  <TextField
                    label="Promotion Code"
                    variant="outlined"
                    id="promo_code"
                    name="promo_code"
                    value={promoCodeInput}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Button
                          className={classes.submit}
                          color="primary"
                          variant="contained"
                          onClick={async () => {
                            applyPromoCode();
                            setApplyPromo(true);
                          }}
                          disabled={!promoCodeInput}
                        >
                          APPLY
                        </Button>
                      )
                    }}
                  />
                  {orderingStore.order.discount && applyPromo && (
                    <Typography variant="body1" color="primary">
                      Your code is accepted!
                    </Typography>
                  )}
                  {!orderingStore.order.discount && applyPromo && (
                    <Typography variant="body1" color="error">
                      Invalid code!
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Box>

            <Box className={classes.orderDetail}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                YOUR ORDER SUMMARY
              </Typography>
              <OrderSummary order={orderingStore.order} />
            </Box>
          </Grid>
        );
    }
  });

  let steps: string[] = [];
  let stepsString: string[] = [];
  let contents: any[] = [];
  steps = acceptTip ? ['Tips', 'Submit'] : ['Submit'];
  contents = acceptTip ? [tipGuides, submit] : [submit];

  let dialogContent = (
    <Fragment>
      <SubmitSteps
        key="steps"
        activeStep={activeStep}
        steps={stepsString}
        variant={LayoutVariant.laptop}
      />
      {contents[activeStep - 1]}
    </Fragment>
  );

  return useObserver(() => (
    <Dialog
      onClose={handleOnClose}
      open
      scroll="paper"
      fullWidth={true}
      maxWidth="md"
      disableBackdropClick={orderingStore.submitState === ViewState.LOADING}
      classes={{ paper: classes.paper }}
    >
      <DialogTitle disableTypography>
        <Typography variant="h6">Submit your order</Typography>
        <IconButton
          aria-label="close"
          onClick={handleOnClose}
          className={classes.close}
          disabled={orderingStore.submitState === ViewState.LOADING}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid
          container
          justify="center"
          alignItems="center"
          className={classes.submitContainer}
        >
          {dialogContent}
        </Grid>
      </DialogContent>
      {orderingStore.submitState !== ViewState.OK &&
        !orderingStore.isNoDriverError && (
          <DialogActions>
            <Button
              variant="contained"
              disabled={
                activeStep === 1 ||
                orderingStore.submitState === ViewState.LOADING
              }
              onClick={() => setActiveStep(activeStep - 1)}
              color="primary"
            >
              Back
            </Button>
            <Button
              variant="contained"
              disabled={
                activeStep === steps.length ||
                orderingStore.submitState === ViewState.LOADING ||
                orderingStore.deliveryQuoteState === ViewState.LOADING
              }
              onClick={() => setActiveStep(activeStep + 1)}
              color="primary"
            >
              Next
            </Button>
          </DialogActions>
        )}
    </Dialog>
  ));
};

export default SubmitKioskOrder;
